import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api';
import RightSidebar from './RightSidebar';
import VehicleOnboardingForm from './VehicleOnboardingForm';
import VehicleInsights from './VehicleInsights';
import { vehicleStatusConfig } from '../config';
// MODIFICATION: Import the Download icon
import { Plus, Search, Download } from 'lucide-react';


const VehicleTable = ({ vehicles, onAddVehicle, onVehicleClick, searchTerm, setSearchTerm, fromDate, setFromDate, toDate, setToDate, onDownloadCsv, statusFilter, setStatusFilter, onToggleCharging }) => (
  <div className="bg-white dark:bg-neutral-800 shadow-xl rounded-2xl p-6 sm:p-8">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-neutral-800 dark:text-white">
          Vehicle Management
        </h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          Onboard new vehicles and view their status.
        </p>
      </div>
    </div>

    {/* NEW: Filter and Action Controls */}
    <div className="flex flex-col sm:flex-row flex-wrap items-center justify-start gap-4 mb-6">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
        <input
          type="text"
          placeholder="Search by no, chassis, motor..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 w-full sm:w-auto"
        />
      </div>

      {/* Date Filters */}
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="px-4 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <span className="text-neutral-500 dark:text-neutral-400">to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          min={fromDate}
          className="px-4 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* NEW: Status Filter */}
      <div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={onDownloadCsv}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
        >
          <Download className="w-5 h-5" />
          <span>Download CSV</span>
        </button>
        <button
          onClick={onAddVehicle}
          className="flex items-center bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
        >
          <Plus size={20} className="mr-2" />
          Onboard Vehicle
        </button>
      </div>
    </div>

    <div className="overflow-x-auto">
      <table className="min-w-full leading-normal">
        <thead>
          <tr className="border-b-2 border-neutral-200 dark:border-neutral-700">
            <th className="px-6 py-4 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
              Onboarding Date
            </th>
            <th className="px-6 py-4 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
              Vehicle No
            </th>
            <th className="px-6 py-4 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
              Chassis No
            </th>
            {/* <th className="px-6 py-4 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
              Motor No
            </th> */}
            <th className="px-6 py-4 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
              Partner ID
            </th>
            <th className="px-6 py-4 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-4 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
              Charging
            </th>
            <th className="px-6 py-4 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {vehicles.map((vehicle) => (
            <tr
              key={vehicle.id}
              className="hover:bg-neutral-100 dark:hover:bg-neutral-700/50 transition-colors duration-200 border-b border-neutral-200 dark:border-neutral-700">
              <td className="px-6 py-5 text-sm">
                <p className="text-neutral-900 dark:text-white whitespace-no-wrap">
                  {new Date(vehicle.onboarding_date).toLocaleDateString()}
                </p>
              </td>
              <td className="px-6 py-5 text-sm">
                <p className="text-neutral-900 dark:text-white whitespace-no-wrap font-medium">
                  {vehicle.vehicle_no}
                </p>
              </td>
              <td className="px-6 py-5 text-sm">
                <p className="text-neutral-900 dark:text-white whitespace-no-wrap font-medium">
                  {vehicle.chassis_number}
                </p>
              </td>
              <td className="px-6 py-5 text-sm">
                <p className="text-neutral-900 dark:text-white whitespace-no-wrap">
                  {vehicle.partner_id || 'N/A'}
                </p>
              </td>
              <td className="px-6 py-5 text-sm">
                <span
                  className={`relative inline-block px-3 py-1 font-semibold leading-tight ${vehicleStatusConfig[vehicle.status]?.styles ||
                    'text-gray-900 bg-gray-200'
                    } rounded-full`}
                >
                  <span className="relative">{vehicle.status}</span>
                </span>
              </td>
              <td className="px-6 py-5 text-sm">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={vehicle.charging_status}
                    onChange={() => onToggleCharging(vehicle)}
                  />
                  <div
                    className={`w-11 h-6 rounded-full transition-all relative ${vehicle.charging_status ? 'bg-green-500' : 'bg-red-400'
                      }`}
                  >
                    <div
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${vehicle.charging_status ? 'translate-x-5' : ''
                        }`}
                    />
                  </div>
                </label>
              </td>
              <td className="px-6 py-5 text-sm">
                <button
                  onClick={() => onVehicleClick(vehicle)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow transition"
                >
                  View Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);


const VehicleManagementView = () => {
  const [vehicles, setVehicles] = useState([]);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarContent, setSidebarContent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // NEW: State for date filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // NEW: State for status filter
  const [statusFilter, setStatusFilter] = useState('');

  const fetchVehicles = useCallback(async () => {
    try {
      const response = await api.get('/vehicles');
      let vehiclesData = Array.isArray(response.data) ? response.data : [];
      setVehicles(vehiclesData);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    }
  }, []);


  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const handleVehicleAdded = () => {
    fetchVehicles();
    setSidebarOpen(false);
  };

  const handleVehicleClick = (vehicle) => {
    setSidebarContent(
      <VehicleInsights vehicle={vehicle} onStatusChange={fetchVehicles} />,
    );
    setSidebarOpen(true);
  };


  const handleAddVehicleClick = () => {
    setSidebarContent(
      <VehicleOnboardingForm onVehicleAdded={handleVehicleAdded} />,
    );
    setSidebarOpen(true);
  };
  const handleToggleCharging = async (vehicle) => {
    try {
      const updatedStatus = !vehicle.charging_status;

      await api.patch(`/vehicles/vehicle/${vehicle.id}/charging-status`, {
        charging_status: updatedStatus,
      });

      // Optimistically update local state so toggle feels instant
      setVehicles(prev =>
        prev.map(v =>
          v.id === vehicle.id ? { ...v, charging_status: updatedStatus } : v
        )
      );

      // Optionally re-fetch from backend to stay in sync
      // fetchVehicles();
    } catch (error) {
      console.error("Error updating charging status:", error);
    }
  };



  // MODIFIED: Filter logic for multiple fields, date range, and status
  const filteredVehicles = useMemo(() => {
    return vehicles.filter(vehicle => {
      const searchLower = searchTerm.toLowerCase();

      const searchMatch = (
        (vehicle.vehicle_no || '').toLowerCase().includes(searchLower) ||
        (vehicle.chassis_number || '').toLowerCase().includes(searchLower) ||
        (vehicle.motor_number || '').toLowerCase().includes(searchLower)
      );

      // Date filter
      let dateMatch = true;
      if (fromDate || toDate) {
        if (!vehicle.onboarding_date) return false;
        const onboardingDate = new Date(vehicle.onboarding_date);
        const start = fromDate ? new Date(fromDate) : null;
        const end = toDate ? new Date(toDate) : null;

        if (start) start.setUTCHours(0, 0, 0, 0);
        if (end) end.setUTCHours(23, 59, 59, 999);

        dateMatch = (!start || onboardingDate >= start) && (!end || onboardingDate <= end);
      }

      // Status filter
      let statusMatch = true;
      if (statusFilter) {
        statusMatch = (vehicle.status || '').toLowerCase() === statusFilter.toLowerCase();
      }

      return searchMatch && dateMatch && statusMatch;
    });
  }, [vehicles, searchTerm, fromDate, toDate, statusFilter]);

  // NEW: Handler for CSV Download
  const handleDownloadCsv = () => {
    if (filteredVehicles.length === 0) {
      alert("No data to download.");
      return;
    }

    const headers = ['Vehicle No', 'Chassis No', 'Motor No', 'Partner ID', 'Status', 'Onboarding Date'];

    const formatCsvCell = (cellData) => {
      const stringData = String(cellData ?? ''); // Handle null/undefined
      if (stringData.includes(',') || stringData.includes('"') || stringData.includes('\n')) {
        return `"${stringData.replace(/"/g, '""')}"`;
      }
      return stringData;
    };

    const csvRows = [
      headers.join(','),
      ...filteredVehicles.map(vehicle => [
        formatCsvCell(vehicle.vehicle_no),
        formatCsvCell(vehicle.chassis_number),
        formatCsvCell(vehicle.motor_number),
        formatCsvCell(vehicle.partner_id || 'N/A'),
        formatCsvCell(vehicle.status),
        formatCsvCell(new Date(vehicle.onboarding_date).toLocaleDateString()),
      ].join(','))
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `vehicles-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="h-full">
      <VehicleTable
        vehicles={filteredVehicles}
        onAddVehicle={handleAddVehicleClick}
        onVehicleClick={handleVehicleClick}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        onDownloadCsv={handleDownloadCsv}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        onToggleCharging={handleToggleCharging}
      />
      <RightSidebar
        isOpen={isSidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        {sidebarContent}
      </RightSidebar>
    </div>
  );
};


export default VehicleManagementView;
